import { Component, Input, ChangeDetectionStrategy, input, computed, inject } from '@angular/core';

import { v4 as uuid } from 'uuid';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { FinnairBoundItem, FinnairPassengerItem } from '@fcom/dapi/api/models';
import { FinnairServiceItemWithUpsell } from '@fcom/dapi/interfaces';
import { getIconForServiceCategory } from '@fcom/dapi/utils';
import { TagTheme } from '@fcom/ui-components/index';
import { FinnairThemeService } from '@fcom/common/services';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { isLowQuota } from '../../utils';
import { boundHasConfirmedServiceSelection } from '../../../../utils/common-booking.utils';

@Component({
  selector: 'fin-included-product-item',
  styleUrls: ['./included-product-item.component.scss'],
  templateUrl: './included-product-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class IncludedProductItemComponent {
  private themeService = inject(FinnairThemeService);

  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TagTheme = TagTheme;

  uuid = uuid();

  @Input() category: string;
  @Input() buttonTranslationKey: string;
  @Input() additionalTitle: string;
  @Input() includePaddings = true;
  @Input() expandOpen = false;
  @Input() disabled = false;
  @Input() displaySeatReassignMessage = false;
  @Input() isShortHaulFlight: boolean;
  @Input() passengers: FinnairPassengerItem[];
  @Input() showPendingServicesInTitles = false;
  @Input() showUnpaidTag = false;

  service = input<FinnairServiceItemWithUpsell>();
  bound = input<FinnairBoundItem>();
  enableQuotaTag = input(false);
  addSelectedItemsToQuota = input(false);

  readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  readonly displayedQuotaCount = computed<number | undefined>(() => {
    const service = this.service();
    const bound = this.bound();
    if (!service) {
      return undefined;
    }

    const { categoryLevelQuotaCount } = service;

    if (bound && boundHasConfirmedServiceSelection(service, bound)) {
      return undefined;
    }

    if (categoryLevelQuotaCount === undefined) {
      return undefined;
    }

    const total = this.addSelectedItemsToQuota()
      ? categoryLevelQuotaCount + (service.unpaidQuantity ?? 0)
      : categoryLevelQuotaCount;

    return isLowQuota(total) ? total : undefined;
  });

  getIconForService(): SvgLibraryIcon {
    return getIconForServiceCategory(this.category);
  }
}
